/**
 * 全局配置：从 import.meta.env / passthrough env 读取业务变量。
 *
 * 业务配置 key 不使用 VITE_ 前缀；保留前缀的 VITE_DEV_* 与 VITE_APP_ENV 仅服务于
 * vite.config.ts 自身（开发代理、APP_ENV 注入）。运行时通过 getEnv() 统一读取。
 *
 * 不做 fallback 兜底：任何必填 env 缺失或空值，启动时直接抛错（fail-fast），
 * 避免运行时拿到空配置静默走到线上才暴露问题。
 *
 */
import { getEnv } from '@/utils/env'

export type Language = 'zh-CN' | 'en-US' | 'ja-JP'
export type ListenMode = 'manual' | 'realtime'
export type BinaryVersion = 1 | 2 | 3

export interface AppConfig {
  // 连接地址
  openApiBaseUrl: string

  wsUrl: string

  // 接入信息
  accessKeyId: string
  accessKeySecret: string
  integrationAppId: string
  endUserId: string

  // 设备 / 客户端
  defaultLogicalDeviceId: string

  // 模式
  defaultMobileMode: ListenMode
  defaultDesktopMode: ListenMode
  manualUplinkGateDesktopEnabled: boolean
  manualUplinkGateIosEnabled: boolean
  manualUplinkGateAndroidEnabled: boolean
  defaultProtocolVersion: BinaryVersion

  // 采集参数
  defaultFrameDurationMs: number

  // 下播输出增益；空环境变量表示使用 SDK 平台默认值
  outputGain: {
    ios?: number
    android?: number
    desktop?: number
  }

  // MCP
  mcpEnabledDefault: boolean

  // 交流语言对应的角色
  soulIdByLanguage: Record<Language, string>

}

/** 允许的采集帧长（不在 SDK 侧强制约束；非法值会回退到默认值）。 */
const ALLOWED_FRAME_DURATION_MS = [20, 30, 40, 60] as const
const DEFAULT_FRAME_DURATION_MS = 30

// ============ env 校验：缺失或空值直接抛错 ============

/** 必填字符串：空字符串视为缺失 */
function requireStr(key: string): string {
  const val = getEnv(key)
  if (typeof val !== 'string' || val.length === 0) {
    throw new Error(`[config] 缺少必填环境变量: ${key}`)
  }
  return val
}

/** 必填 URL：去掉末尾多余的 /，避免拼接时出现双斜杠 */
function requireUrl(key: string): string {
  return requireStr(key).replace(/\/+$/, '')
}

/** 可选 URL：开发环境留空时使用当前页面同源地址。 */
function requireOptionalUrl(key: string): string {
  const val = getEnv(key)
  if (typeof val !== 'string' || val.length === 0) {
    return window.location.origin
  }
  return val.replace(/\/+$/, '')
}

/** 必填数字：空字符串 / 非数字 / ≤0 视为缺失 */
function requireNum(key: string): number {
  const raw = requireStr(key)
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`[config] 环境变量 ${key} 不是有效的正数: "${raw}"`)
  }
  return n
}

/** 可选输出增益：空值表示交给 SDK，填写时只接受 1.0～2.0。 */
function readOptionalOutputGain(key: string): number | undefined {
  const raw = getEnv(key)
  if (typeof raw !== 'string' || raw.trim() === '') {
    return undefined
  }
  const value = Number(raw)
  if (!Number.isFinite(value) || value < 1 || value > 5) {
    throw new Error(`[config] 环境变量 ${key} 必须是 1.0～5.0，当前值: "${raw}"`)
  }
  return value
}

/** 非法帧长回退到默认值，避免阻断 Demo 启动。 */
function readFrameDurationMs(key: string): number {
  const raw = requireStr(key)
  const value = Number(raw)
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`[config] 环境变量 ${key} 不是有效的正数: "${raw}"`)
  }
  if (ALLOWED_FRAME_DURATION_MS.includes(value as (typeof ALLOWED_FRAME_DURATION_MS)[number])) {
    return value
  }
  console.warn(
    `[config] 环境变量 ${key}="${raw}" 不在允许列表 ${ALLOWED_FRAME_DURATION_MS.join(' / ')} 中，已回退到 ${DEFAULT_FRAME_DURATION_MS}ms`,
  )
  return DEFAULT_FRAME_DURATION_MS
}

/** 必填布尔：只接受 "true" / "false"（大小写不敏感） */
function requireBool(key: string): boolean {
  const raw = requireStr(key)
  const lower = raw.toLowerCase()
  if (lower === 'true') return true
  if (lower === 'false') return false
  throw new Error(`[config] 环境变量 ${key} 不是有效的布尔值（true/false）: "${raw}"`)
}

/** 必填枚举：值必须在给定的候选列表里 */
function requireEnum<T extends string>(key: string, candidates: readonly T[]): T {
  const raw = requireStr(key)
  if (!candidates.includes(raw as T)) {
    throw new Error(
      `[config] 环境变量 ${key} 不是有效值（${candidates.join(' / ')}）: "${raw}"`,
    )
  }
  return raw as T
}

/** 启动时一次性校验各交流语言的角色配置。 */
function readSoulIdByLanguage(): Record<Language, string> {
  return {
    'zh-CN': requireStr('SOUL_ID_ZH_CN'),
    'en-US': requireStr('SOUL_ID_EN_US'),
    'ja-JP': requireStr('SOUL_ID_JA_JP'),
  }
}

function readEnv(): AppConfig {
  return {
    openApiBaseUrl: requireOptionalUrl('OPENAPI_BASE_URL'),
    wsUrl: requireUrl('WS_URL'),
    accessKeyId: requireStr('ACCESS_KEY_ID'),
    accessKeySecret: requireStr('ACCESS_KEY_SECRET'),
    integrationAppId: requireStr('INTEGRATION_APP_ID'),
    endUserId: requireStr('END_USER_ID'),
    defaultLogicalDeviceId: requireStr('LOGICAL_DEVICE_ID'),
    defaultMobileMode: requireEnum('DEFAULT_MOBILE_MODE', ['manual', 'realtime'] as const),
    defaultDesktopMode: requireEnum('DEFAULT_DESKTOP_MODE', ['manual', 'realtime'] as const),
    manualUplinkGateDesktopEnabled: requireBool('MANUAL_UPLINK_GATE_DESKTOP_ENABLED'),
    manualUplinkGateIosEnabled: requireBool('MANUAL_UPLINK_GATE_IOS_ENABLED'),
    manualUplinkGateAndroidEnabled: requireBool('MANUAL_UPLINK_GATE_ANDROID_ENABLED'),
    defaultProtocolVersion: Number(
      requireEnum('PROTOCOL_VERSION', ['1', '2', '3'] as const),
    ) as BinaryVersion,
    defaultFrameDurationMs: readFrameDurationMs('DEFAULT_FRAME_DURATION_MS'),
    outputGain: {
      ios: readOptionalOutputGain('OUTPUT_GAIN_IOS'),
      android: readOptionalOutputGain('OUTPUT_GAIN_ANDROID'),
      desktop: readOptionalOutputGain('OUTPUT_GAIN_DESKTOP'),
    },
    mcpEnabledDefault: requireBool('MCP_ENABLED_DEFAULT'),

    // 交流语言只用于选择对应角色。
    soulIdByLanguage: readSoulIdByLanguage(),
  }
}

export const appConfig: AppConfig = readEnv()
